import React, { useEffect, useState } from "react";
import {
    Box, Chip, CircularProgress, Collapse, Divider, IconButton, Paper,
    Table, TableBody, TableCell, TableHead, TableRow, Typography,
    LinearProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import FolderSpecialRoundedIcon from "@mui/icons-material/FolderSpecialRounded";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import { financeApi, fmtKRWFull } from "../../api/financeApi";
import { formatPercent } from "../../../../util/numberFormat";
import { ds } from "../../../../styles/designSystem";
import {
    VendorCell, TransactionTitleCell, ApprovalLinkChip,
} from "../../components/FinanceLinkProvider";
import { useTableSort } from "../../../../util/useTableSort";
import TablePaginator, { DEFAULT_PAGE_SIZE, TableSizeSelect } from "../../../../component/common/TablePaginator";
import ResizableTable from "../../../../component/common/ResizableTable";
import LoadingDots from "../../../../component/common/LoadingDots";

/* ── 프로젝트 카드 ───────────────────────────────────────────────── */
function ProjectCard({ project }) {
    // 공통 정렬 훅 — 거래내역 테이블에 일관된 ▲/▼/⇅ UX 적용
    const { toggleSort, sortLabel, sortRows } = useTableSort("transactionDate", "desc");
    const [open, setOpen]           = useState(false);
    const [txLoading, setTxLoading] = useState(false);
    const [txList, setTxList]       = useState([]);
    const [txPage, setTxPage]       = useState(1);
    const [txPageSize, setTxPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [txTotal, setTxTotal]     = useState(0);
    const [txTotalPages, setTxTotalPages] = useState(1);
    const [fetched, setFetched]     = useState(false); // 첫 fetch 여부

    const profit = project.netProfit;
    const hasData = project.totalIncome > 0 || project.totalExpense > 0;
    const expenseRatio = project.totalIncome > 0
        ? (project.totalExpense / project.totalIncome) * 100
        : 0;

    const loadTx = (page, size) => {
        setTxLoading(true);
        financeApi.getProjectTransactions(project.channelId, page - 1, size)
            .then(res => {
                setTxList(res?.content ?? []);
                setTxTotal(res?.totalElements ?? 0);
                setTxTotalPages(res?.totalPages ?? 1);
                setFetched(true);
            })
            .catch(() => setTxList([]))
            .finally(() => setTxLoading(false));
    };

    const handleToggle = () => {
        const next = !open;
        setOpen(next);
        if (next && !fetched) {
            loadTx(1, txPageSize);
        }
    };

    const handlePageChange = (p) => {
        setTxPage(p);
        loadTx(p, txPageSize);
    };

    const handlePageSizeChange = (s) => {
        setTxPageSize(s);
        setTxPage(1);
        loadTx(1, s);
    };

    return (
        <Paper sx={ds.card}>
            {/* 요약 헤더 */}
            <Box sx={{ px: 2.5, py: 2, display: "flex", alignItems: "flex-start", gap: 2 }}>
                <Box sx={{
                    width: 40, height: 40, borderRadius: 1.5,
                    bgcolor: "#dbeafe", display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                }}>
                    <FolderSpecialRoundedIcon sx={{ color: "#2563eb", fontSize: 20 }} />
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#1e293b" }}>
                        {project.channelName}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                        채널 #{project.channelId} · 프로젝트 채널
                    </Typography>
                    {hasData && (
                        <Box sx={{ mt: 1 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.4 }}>
                                <Typography sx={{ fontSize: 11, color: "#64748b" }}>지출 비율</Typography>
                                <Typography sx={{ fontSize: 11, fontWeight: 700,
                                    color: expenseRatio > 90 ? "#dc2626" : expenseRatio > 70 ? "#d97706" : "#16a34a" }}>
                                    {formatPercent(expenseRatio, 0)}
                                </Typography>
                            </Box>
                            <LinearProgress variant="determinate" value={Math.min(expenseRatio, 100)}
                                sx={{
                                    height: 5, borderRadius: 3, bgcolor: "#f1f5f9",
                                    "& .MuiLinearProgress-bar": {
                                        borderRadius: 3,
                                        bgcolor: expenseRatio > 90 ? "#dc2626" : expenseRatio > 70 ? "#d97706" : "#2563eb",
                                    },
                                }} />
                        </Box>
                    )}
                </Box>

                {hasData && (
                    <Box sx={{ textAlign: "right", mr: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, justifyContent: "flex-end" }}>
                            <TrendingUpIcon sx={{ fontSize: 13, color: ds.finance.income }} />
                            <Typography sx={{ fontSize: 12, color: "#64748b" }}>수입</Typography>
                        </Box>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: ds.finance.income }}>
                            +{fmtKRWFull(project.totalIncome)}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, justifyContent: "flex-end", mt: 0.5 }}>
                            <TrendingDownIcon sx={{ fontSize: 13, color: ds.finance.expense }} />
                            <Typography sx={{ fontSize: 12, color: "#64748b" }}>지출</Typography>
                        </Box>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: ds.finance.expense }}>
                            -{fmtKRWFull(project.totalExpense)}
                        </Typography>
                    </Box>
                )}

                <Box sx={{ textAlign: "right", minWidth: 120 }}>
                    <Typography sx={{ fontSize: 11, color: "#64748b" }}>현재 잔액</Typography>
                    {hasData ? (
                        <Typography sx={{ fontSize: 18, fontWeight: 800, color: profit >= 0 ? ds.finance.balance : ds.finance.expense }}>
                            {profit >= 0 ? "+" : ""}{fmtKRWFull(profit)}
                        </Typography>
                    ) : (
                        <Typography sx={{ fontSize: 13, color: "#94a3b8", fontWeight: 500 }}>
                            거래 내역 없음
                        </Typography>
                    )}
                </Box>

                <IconButton size="small" onClick={handleToggle}>
                    {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </Box>

            {/* 거래 내역 — 토글 시 서버 조회 */}
            <Collapse in={open}>
                <Divider />
                <Box sx={{ p: 2.5 }}>
                    {txLoading ? (
                        <LoadingDots compact label="거래 내역을 불러오고 있어요" />
                    ) : !fetched ? null : txList.length === 0 ? (
                        <Typography sx={{ fontSize: 13, color: "#94a3b8", textAlign: "center", py: 2 }}>
                            이 채널에 등록된 수입/지출 내역이 없습니다.<br />
                            수입/지출 내역 탭에서 이 채널을 선택해 등록하세요.
                        </Typography>
                    ) : (
                        <>
                            {/* N개씩 보기 — 표 우측 상단 고정(공용 컨트롤) */}
                            <Box sx={{ display: "flex", mb: 0.8 }}>
                                <TableSizeSelect value={txPageSize} onChange={handlePageSizeChange} />
                            </Box>
                            <ResizableTable size="small" sx={ds.tableSx}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("transactionDate")}>{sortLabel("transactionDate", "일자")}</TableCell>
                                        <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("title")}>{sortLabel("title", "항목")}</TableCell>
                                        <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("vendorName")}>{sortLabel("vendorName", "거래처")}</TableCell>
                                        <TableCell align="right" sx={{ cursor: "pointer" }} onClick={() => toggleSort("amount")}>{sortLabel("amount", "금액")}</TableCell>
                                        <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("type")}>{sortLabel("type", "구분")}</TableCell>
                                        <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("deptName")}>{sortLabel("deptName", "팀")}</TableCell>
                                        <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("category")}>{sortLabel("category", "카테고리")}</TableCell>
                                        <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("createdByName")}>{sortLabel("createdByName", "등록자")}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sortRows(txList, {
                                        amount: r => (r.type === "INCOME" ? 1 : -1) * (r.amount || 0),
                                    }).map(tx => (
                                        <TableRow key={tx.id}>
                                            <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>{tx.transactionDate}</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 500 }}>
                                                <TransactionTitleCell row={tx} />
                                            </TableCell>
                                            <TableCell align="center" sx={{ fontSize: 12 }}>
                                                <VendorCell vendorId={tx.vendorId} vendorName={tx.vendorName} />
                                            </TableCell>
                                            <TableCell align="right" sx={{
                                                fontWeight: 700,
                                                color: tx.type === "INCOME" ? ds.finance.income : ds.finance.expense,
                                                whiteSpace: "nowrap",
                                            }}>
                                                {tx.type === "INCOME" ? "+" : "-"}{fmtKRWFull(tx.amount)}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip label={tx.typeLabel} size="small" sx={{
                                                    bgcolor: tx.type === "INCOME" ? "#f0fdf4" : "#fef2f2",
                                                    color:  tx.type === "INCOME" ? ds.finance.income  : ds.finance.expense,
                                                    fontWeight: 600,
                                                }} />
                                            </TableCell>
                                            <TableCell align="center">{tx.deptName || "—"}</TableCell>
                                            <TableCell align="center" sx={{ fontSize: 12, color: "#64748b" }}>
                                                {tx.approvalId
                                                    ? <ApprovalLinkChip row={tx} />
                                                    : (tx.category || "—")}
                                            </TableCell>
                                            <TableCell align="center">{tx.createdByName}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </ResizableTable>
                            <TablePaginator
                                totalPages={txTotalPages}
                                total={txTotal}
                                pageSize={txPageSize}
                                page={txPage}
                                onPageChange={handlePageChange}
                            />
                        </>
                    )}
                </Box>
            </Collapse>
        </Paper>
    );
}

/* ── 메인 탭 ─────────────────────────────────────────────────────── */
export default function ProjectPnLTab() {
    const [projects, setProjects]         = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading]           = useState(true);
    const [endedOpen, setEndedOpen]       = useState(false);

    useEffect(() => {
        setLoading(true);
        financeApi.getProjectSummaries()
            .then(res => {
                const list = Array.isArray(res) ? res : (res?.content ?? []);
                setProjects(list);
                setTotalElements(list.length);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <LoadingDots label="프로젝트 채널을 불러오고 있어요" />;

    const active = projects.filter(p => !p.deadlineExpired);
    const ended  = projects.filter(p => p.deadlineExpired);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                <Typography sx={{ ...ds.typography.sectionTitle, flex: 1 }}>
                    프로젝트별 손익 ({totalElements}개 채널)
                </Typography>
            </Box>

            {totalElements === 0 ? (
                <Paper sx={{ ...ds.cardPadded, textAlign: "center" }}>
                    <Typography color="text.secondary">등록된 프로젝트 채널이 없습니다.</Typography>
                    <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.5 }}>
                        좌측 '채널' 탭에서 프로젝트를 생성해주세요.
                    </Typography>
                </Paper>
            ) : (
                <>
                    {active.map(p => <ProjectCard key={p.channelId} project={p} />)}

                    {ended.length > 0 && (
                        <Box>
                            <Box
                                onClick={() => setEndedOpen(v => !v)}
                                sx={{
                                    display: "flex", alignItems: "center", gap: 1, cursor: "pointer",
                                    px: 1, py: 0.5, borderRadius: 1,
                                    "&:hover": { bgcolor: "#f8fafc" },
                                }}
                            >
                                <IconButton size="small" sx={{ p: 0 }}>
                                    {endedOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                </IconButton>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#64748b" }}>
                                    종료된 프로젝트 ({ended.length}개)
                                </Typography>
                                <Chip label="마감일 경과" size="small"
                                    sx={{ bgcolor: "#f1f5f9", color: "#64748b", fontWeight: 600, fontSize: 11 }} />
                            </Box>
                            <Collapse in={endedOpen}>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                                    {ended.map(p => <ProjectCard key={p.channelId} project={p} />)}
                                </Box>
                            </Collapse>
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
}
